import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { CartProvider } from "./lib/cart";
import { I18nProvider } from "./lib/i18n";
import { api } from "./lib/api";
import { bumpSeoSettings } from "./components/SEO";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import CookieBanner from "./components/CookieBanner";
import CartDrawer from "./components/CartDrawer";
import BackToTop from "./components/BackToTop";
import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Account from "./pages/Account";
import Admin from "./pages/Admin";
import Legal from "./pages/Legal";
import FAQ from "./pages/FAQ";
import { BlogList, BlogDetail } from "./pages/Blog";
import DownloadLanding from "./pages/DownloadLanding";
import Contact from "./pages/Contact";
import Guide from "./pages/Guide";
import NotFound from "./pages/NotFound";
import Redeem from "./pages/Redeem";
import Gaming from "./pages/Gaming";
import GamingProductDetail from "./pages/GamingProductDetail";
import AutoTranslate from "./components/AutoTranslate";

function App() {
  // Bootstrap global site settings (SEO defaults, brand, analytics IDs) into window.__siteSettings
  useEffect(() => {
    api.get(`/site-settings?_=${Date.now()}`).then((r) => {
      window.__siteSettings = r.data || {};
      injectAnalytics(r.data || {});
      injectOrganizationJsonLd(r.data || {});
      bumpSeoSettings();
      // Apply custom favicon (from admin) to document head
      const fav = (r.data || {}).favicon;
      if (fav) {
        document.querySelectorAll('link[rel*="icon"]').forEach((el) => el.remove());
        const link = document.createElement("link");
        link.rel = "icon";
        // pick mime by extension
        const ext = (fav.split("?")[0].split(".").pop() || "").toLowerCase();
        link.type = ext === "svg" ? "image/svg+xml" : ext === "ico" ? "image/x-icon" : `image/${ext || "png"}`;
        link.href = fav;
        document.head.appendChild(link);
        // Apple touch icon (used by iOS home-screen)
        const apple = document.createElement("link");
        apple.rel = "apple-touch-icon";
        apple.href = fav;
        document.head.appendChild(apple);
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop/>
              <AutoTranslate/>
              <Header/>
              <CartDrawer/>
              <main className="min-h-screen">
                <Routes>
                  <Route path="/" element={<Home/>}/>
                  <Route path="/produkty" element={<Products/>}/>
                  <Route path="/produkty/kategorie/:slug" element={<Products/>}/>
                  <Route path="/produkt/:slug" element={<ProductDetail/>}/>
                  <Route path="/pokladna" element={<Checkout/>}/>
                  <Route path="/dekujeme" element={<OrderSuccess/>}/>
                  <Route path="/order-success" element={<OrderSuccess/>}/>
                  <Route path="/prihlaseni" element={<Login/>}/>
                  <Route path="/registrace" element={<Register/>}/>
                  <Route path="/zapomenute-heslo" element={<ForgotPassword/>}/>
                  <Route path="/reset-hesla" element={<ResetPassword/>}/>
                  <Route path="/ucet" element={<Account/>}/>
                  <Route path="/admin" element={<Admin/>}/>
                  <Route path="/kontakt" element={<Contact/>}/>
                  <Route path="/faq" element={<FAQ/>}/>
                  <Route path="/podpora" element={<Legal slug="podpora"/>}/>
                  <Route path="/blog" element={<BlogList/>}/>
                  <Route path="/blog/:slug" element={<BlogDetail/>}/>
                  <Route path="/gaming" element={<Gaming/>}/>
                  <Route path="/gaming/:slug" element={<GamingProductDetail/>}/>
                  <Route path="/download/:slug" element={<DownloadLanding/>}/>
                  <Route path="/stahnout/:slug" element={<DownloadLanding/>}/>
                  <Route path="/obchodni-podminky" element={<Legal slug="obchodni-podminky"/>}/>
                  <Route path="/gdpr" element={<Legal slug="gdpr"/>}/>
                  <Route path="/cookies" element={<Legal slug="cookies"/>}/>
                  <Route path="/odstoupeni" element={<Legal slug="odstoupeni"/>}/>
                  <Route path="/reklamace" element={<Legal slug="reklamace"/>}/>
                  <Route path="/digital-disclaimer" element={<Legal slug="digital-disclaimer"/>}/>
                  <Route path="/navod/:slug" element={<Guide/>}/>
                  <Route path="/redeem" element={<Redeem/>}/>
                  <Route path="/redeem/:code" element={<Redeem/>}/>
                  <Route path="/poukaz" element={<Redeem/>}/>
                  <Route path="/poukaz/:code" element={<Redeem/>}/>
                  {/* Catch-all 404 — must be last */}
                  <Route path="*" element={<NotFound/>}/>
                </Routes>
              </main>
              <Footer/>
              <CookieBanner/>
              <BackToTop/>
              <Toaster position="top-center" richColors/>
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;

function injectAnalytics(s) {
  // Google Analytics 4
  if (s.seo_ga_id && !document.getElementById("ga4-script")) {
    const sc = document.createElement("script");
    sc.async = true;
    sc.id = "ga4-script";
    sc.src = `https://www.googletagmanager.com/gtag/js?id=${s.seo_ga_id}`;
    document.head.appendChild(sc);
    const inline = document.createElement("script");
    inline.id = "ga4-inline";
    inline.text = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${s.seo_ga_id}', { anonymize_ip: true });`;
    document.head.appendChild(inline);
  }
  // Google Tag Manager
  if (s.seo_gtm_id && !document.getElementById("gtm-script")) {
    const sc = document.createElement("script");
    sc.id = "gtm-script";
    sc.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${s.seo_gtm_id}');`;
    document.head.appendChild(sc);
  }
  // Meta / Facebook Pixel
  if (s.seo_facebook_pixel && !document.getElementById("fb-pixel")) {
    const sc = document.createElement("script");
    sc.id = "fb-pixel";
    sc.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${s.seo_facebook_pixel}');fbq('track','PageView');`;
    document.head.appendChild(sc);
  }
}

function injectOrganizationJsonLd(s) {
  const existing = document.getElementById("org-jsonld");
  if (existing) existing.remove();
  const raw = s.seo_canonical_base || window.location.origin;
  const base = (/^https?:\/\//i.test(raw) ? raw : "https://" + String(raw).replace(/^\/+/, "")).replace(/\/$/, "");
  const data = {
    "@context": "https://schema.org",
    "@type": "OnlineStore",
    name: s.seo_site_name || s.brand_name || "Správný Směr",
    url: base,
    logo: s.logo_url || s.seo_og_image || `${base}/logo.png`,
    description: s.seo_default_description || "",
    address: {
      "@type": "PostalAddress",
      addressLocality: s.footer_city || "Praha",
      addressCountry: "CZ",
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: s.footer_email || "info@spravnysmer.eu",
      contactType: "customer support",
      areaServed: ["CZ", "SK"],
      availableLanguage: ["cs", "sk"],
    },
    sameAs: [s.social_facebook, s.social_instagram, s.social_twitter].filter(Boolean),
  };
  const sc = document.createElement("script");
  sc.type = "application/ld+json";
  sc.id = "org-jsonld";
  sc.text = JSON.stringify(data);
  document.head.appendChild(sc);
}
